import { useEffect, useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchData } from "../../redux/slices/mainDataSlice";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserClock,
  faMagnifyingGlass,
  faPenToSquare,
  faTrash,
  faBuildingColumns,
} from "@fortawesome/free-solid-svg-icons";
import Can from "../../permissions/Can";
import Tooltip from "../../components/Tooltip";

// تنسيق تاريخ وساعة النقل — أرقام غربية دائماً (YYYY-MM-DD HH:MM)
const formatTransferDate = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ").trim();
  const [d, t] = s.split(" ");
  if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return "—";
  return t ? `${d} ${t.slice(0, 5)}` : d;
};

const HalfMrkaz = () => {  const dispatch = useDispatch();
  const navigate = useNavigate();
  const schools = useSelector((state) => state.main.data?.schoolData || []);
  const halfMarkazData = useSelector((state) => state.main.data?.halfMarkazData || []);
  const allTeachers = useSelector((state) => state.main.data?.allteachers || []);

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    if (schools.length === 0 || halfMarkazData.length === 0 || allTeachers.length === 0) {
      dispatch(fetchData()).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [dispatch, schools.length, halfMarkazData.length, allTeachers.length]);

  // Debounce للبحث
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const schoolNameMap = useMemo(() => {
    const map = {};
    schools.forEach((s) => {
      if (s.rgmw) map[String(s.rgmw)] = s.schoolname;
    });
    return map;
  }, [schools]);

  const filteredData = useMemo(() => {
    if (!debouncedQuery.trim()) return halfMarkazData;
    const q = debouncedQuery.trim().toLowerCase();
    return halfMarkazData.filter((emp) =>
      (emp.emp_name || "").toLowerCase().includes(q)
    );
  }, [halfMarkazData, debouncedQuery]);

  const handleDelete = (emp_id, emp_name) => {
    toast(
      (t) => (
        <div style={{ fontFamily: "Cairo, sans-serif", textAlign: "center" }}>
          <div>سيتم حذف سجل النصف مركز لـ:</div>
          <div style={{ color: "#d32f2f", margin: "5px 0" }}>{emp_name}</div>
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <button
              onClick={async () => {
                try {
                  const credentials = JSON.parse(
                    localStorage.getItem("userCredentials")
                  );
                  const token = credentials?.token;

                  await fetch(
                    `https://api.alfares.cloud/api/alfares/updateMainTeacherBeforeDelete/${emp_id}/${token}`,
                    { method: "PUT" }
                  );

                  await fetch(
                    `https://api.alfares.cloud/api/alfares/deleteHalfMrkaz/${emp_id}/${token}`,
                    { method: "DELETE" }
                  );

                  toast.success("تم الحذف بنجاح");
                  dispatch(fetchData());
                } catch {
                  toast.error("فشل في العملية");
                } finally {
                  toast.dismiss(t.id);
                }
              }}
              style={{
                backgroundColor: "#d32f2f",
                color: "white",
                padding: "6px 14px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              نعم
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                backgroundColor: "var(--panel-2)",
                color: "var(--text)",
                padding: "6px 14px",
                border: "1px solid var(--border)",
                borderRadius: "6px",
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 8000 }
    );
  };

  return (
    <div className="halfmrkaz-wrapper anim-fade-in">
      <div className="premium-edit-container">
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <FontAwesomeIcon icon={faUserClock} />
            </div>
            <div className="header-text-box">
              <h2>النصف مركز</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">
                  الموظفون بنظام النصف مركز
                </span>
              </div>
            </div>
          </div>
          <div className="header-tools">
            <div className="global-search-container">
              <div className="input-wrapper">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="search-icon"
                />
                <input
                  type="text"
                  placeholder="ابحث عن اسم الموظف..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="premium-table-container">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ تحميل البيانات...</span>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="quota-empty">لا يوجد موظفون بنظام النصف مركز</div>
          ) : (
            <>
              <div className="results-count">
                {filteredData.length} موظف
              </div>
              <div className="table-wrapper">
                <table className="excel-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>اسم الموظف</th>
                      <th>رقم الهوية</th>
                      <th>المدرسة الحالية</th>
                      <th>المدرسة المشتركة</th>
                      <th>تاريخ النقل</th>
                      <th>إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((emp, index) => (
                      <tr key={emp.emp_id}>
                        <td data-label="#">{index + 1}</td>
                        <td data-label="اسم الموظف" className="cell-name">
                          {emp.emp_name}
                        </td>
                        <td data-label="رقم الهوية">{emp.emp_id}</td>
                        <td data-label="المدرسة الحالية">
                          {schoolNameMap[String(emp.rgmw)] || "غير معروف"}
                        </td>
                        <td data-label="المدرسة المشتركة">
                          {schoolNameMap[String(emp.otherschool_rgmw)] ||
                            "غير معروف"}
                        </td>
                        <td data-label="تاريخ النقل">
                          {formatTransferDate(emp.transfer_date)}
                        </td>
                        <td data-label="إجراءات">
                          <div className="row-actions">
                            <Can module="employees" action="edit">
                              <Tooltip content="تعديل البيانات">
                                <button
                                  className="btn-table btn-table--edit"
                                  onClick={() =>
                                    navigate(`/editHalfEmployee/${emp.emp_id}`)
                                  }
                               
                                  aria-label="تعديل البيانات"
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                              </Tooltip>
                            </Can>
                            <Can module="employees" action="delete">
                              <Tooltip content="حذف النصف مركز">
                                <button
                                  className="btn-table btn-table--delete"
                                  onClick={() =>
                                    handleDelete(emp.emp_id, emp.emp_name)
                                  }
                               
                                  aria-label="حذف النصف مركز"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </Tooltip>
                            </Can>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .halfmrkaz-wrapper {
          padding: 5px;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
        }

        .halfmrkaz-wrapper .premium-header-modern {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px 6px 0 0;
          padding: 16px 20px;
          flex-wrap: wrap;
        }

        .halfmrkaz-wrapper .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .halfmrkaz-wrapper .header-icon-box {
          width: 40px;
          height: 40px;
          border-radius: 6px;
          background: var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: #fff;
          flex-shrink: 0;
        }

        .halfmrkaz-wrapper .header-text-box h2 {
          margin: 0;
          font-size: 17px;
          font-weight: 400;
          font-family: 'Cairo', sans-serif;
        }

        .halfmrkaz-wrapper .breadcrumb-info {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--muted);
          font-size: 12px;
          margin-top: 2px;
        }

        .halfmrkaz-wrapper .separator { margin: 0 4px; }
        .halfmrkaz-wrapper .current-page { color: var(--accent); }

        .halfmrkaz-wrapper .header-tools {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .halfmrkaz-wrapper .global-search-container {
          width: 280px;
          max-width: 100%;
        }

        .halfmrkaz-wrapper .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .halfmrkaz-wrapper .search-icon {
          position: absolute;
          right: 12px;
          color: var(--muted);
          font-size: 14px;
          pointer-events: none;
        }

        .halfmrkaz-wrapper .input-wrapper input {
          width: 100%;
          height: 40px;
          padding: 0 36px 0 12px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--panel-2);
          color: var(--text);
          font-family: 'Cairo', sans-serif;
          font-size: 14px;
          outline: none;
          box-sizing: border-box;
        }

        .halfmrkaz-wrapper .input-wrapper input:focus {
          border-color: var(--accent);
          background: var(--panel);
        }

        .halfmrkaz-wrapper .premium-table-container {
          background: var(--panel);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 6px 6px;
          overflow: hidden;
        }

        .halfmrkaz-wrapper .results-count {
          padding: 10px 16px;
          font-size: 13px;
          color: var(--muted);
          border-bottom: 1px solid var(--border);
          font-family: 'Cairo', sans-serif;
        }

        .halfmrkaz-wrapper .table-wrapper {
          overflow-x: auto;
        }

        .halfmrkaz-wrapper .excel-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          font-family: 'Cairo', sans-serif;
        }

        .halfmrkaz-wrapper .excel-table th,
        .halfmrkaz-wrapper .excel-table td {
          padding: 10px 12px;
          text-align: right;
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }

        .halfmrkaz-wrapper .excel-table thead th {
          background: var(--panel-2);
          color: var(--muted);
          font-weight: 400;
          font-size: 12px;
        }

        .halfmrkaz-wrapper .excel-table tbody tr:hover td {
          background: color-mix(in srgb, var(--accent) 5%, transparent);
        }

        .halfmrkaz-wrapper .cell-name {
          color: var(--accent);
        }

        .halfmrkaz-wrapper .row-actions {
          display: flex;
          gap: 6px;
          justify-content: flex-start;
        }

        .halfmrkaz-wrapper .btn-table {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--panel);
          color: var(--muted);
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s;
        }

        .halfmrkaz-wrapper .btn-table--edit:hover {
          color: var(--accent);
          border-color: var(--accent);
        }

        .halfmrkaz-wrapper .btn-table--delete:hover {
          color: #d32f2f;
          border-color: #d32f2f;
        }

        .halfmrkaz-wrapper .loader-overlay,
        .halfmrkaz-wrapper .quota-empty {
          padding: 40px;
          text-align: center;
          color: var(--muted);
          font-family: 'Cairo', sans-serif;
        }

        @media (max-width: 768px) {
          .halfmrkaz-wrapper {
            padding: 0;
          }

          .halfmrkaz-wrapper .premium-header-modern {
            border-radius: 6px 6px 0 0;
            padding: 14px 12px;
          }

          .halfmrkaz-wrapper .header-text-box h2 {
            font-size: 16px;
          }

          .halfmrkaz-wrapper .breadcrumb-info {
            display: none;
          }

          .halfmrkaz-wrapper .header-tools {
            width: 100%;
          }

          .halfmrkaz-wrapper .global-search-container {
            width: 100%;
          }

          /* بطاقات الهاتف */
          .halfmrkaz-wrapper .excel-table thead {
            display: none;
          }

          .halfmrkaz-wrapper .excel-table,
          .halfmrkaz-wrapper .excel-table tbody,
          .halfmrkaz-wrapper .excel-table tr,
          .halfmrkaz-wrapper .excel-table td {
            display: block;
            width: 100%;
            box-sizing: border-box;
          }

          .halfmrkaz-wrapper .excel-table tbody tr {
            background: var(--panel);
            border: 1px solid var(--border);
            border-radius: 6px;
            margin: 10px;
            width: calc(100% - 20px);
            padding: 4px 0;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          }

          .halfmrkaz-wrapper .excel-table td {
            border: none;
            padding: 6px 14px;
            white-space: normal;
            text-align: right;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .halfmrkaz-wrapper .excel-table td::before {
            content: attr(data-label);
            font-size: 12px;
            color: var(--accent);
            min-width: 90px;
            flex-shrink: 0;
          }

          .halfmrkaz-wrapper .excel-table td[data-label="#"] {
            display: none;
          }

          .halfmrkaz-wrapper .excel-table td[data-label="اسم الموظف"] {
            font-size: 15px;
            padding-top: 10px;
          }

          .halfmrkaz-wrapper .excel-table td[data-label="اسم الموظف"]::before {
            display: none;
          }

          .halfmrkaz-wrapper .excel-table td[data-label="إجراءات"]::before {
            display: none;
          }

          .halfmrkaz-wrapper .excel-table td[data-label="إجراءات"] {
            justify-content: flex-end;
            padding-bottom: 10px;
          }

          .halfmrkaz-wrapper .row-actions {
            width: 100%;
            justify-content: flex-end;
          }

          .halfmrkaz-wrapper .btn-table {
            width: 38px;
            height: 38px;
          }
        }
      `}</style>
    </div>
  );
};

export default HalfMrkaz;
